import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../config/consts';
import { useDataGrid } from '../../helpers/dataGrid';
import useBreakpoints from '../../helpers/useBreakpoints';
import { hasActivableGSMService } from '../../modules/devices/helpers/helpers';
import { IDevice } from '../../modules/devices/store/deviceStore';
import { isFetchingMessages } from '../../modules/localization/selectors';
import { getSiteData } from '../../modules/sites/selectors';
import ApartmentNameChip from '../ApartmentNameChip';
import DataGrid from '../DataGrid';
import NameColumn from '../DataGrid/NameColumn';
import DeviceModelName from '../DeviceModelName';
import DeviceStatus from '../DeviceStatus';
import DeviceTypeIcon from '../DeviceTypeIcon';
import IconServices from '../IconServices';
import DataGridRowMenu from './DataGridRowMenu/DataGridRowMenu';
import messages from './messages';
import NoRowsOverlay from './NoRowsOverlay';

interface IFilter {
  fulltext: string;
}

export interface ICompanyAdminDevicesDataGridProps {
  companyId: number;
  data?: IDevice[];
  didInvalid?: boolean;
  filter: IFilter;
  isFetching: boolean;
  // eslint-disable-next-line max-params
  onLoadDevices: (
    companyId: unknown,
    siteId: unknown,
    groupId: unknown,
    userId: unknown,
    page: number,
    rowsPerPage: number,
    filter: IFilter,
    order?: string
  ) => void;
  order?: string;
  page: number;
  resultsFiltered: number;
  resultsTotal: number;
  rowsPerPage: number;
  userId: string | number;
}

const CompanyAdminDevicesDataGrid = ({
  companyId,
  data = [],
  didInvalid,
  filter,
  isFetching,
  onLoadDevices,
  order,
  page,
  resultsFiltered,
  resultsTotal,
  rowsPerPage,
  userId,
}: ICompanyAdminDevicesDataGridProps) => {
  const { onOrderBy, onRowsPerPageChange, onSelectPage } = useDataGrid(
    onLoadDevices,
    {
      companyId,
      emptyGroup: true,
      emptySite: true,
      filter,
      order,
      page,
      rowsPerPage,
      userId,
    },
    didInvalid,
    isFetching
  );

  const { upMd, upSm } = useBreakpoints();
  const isLocalizationLoaded = useSelector(isFetchingMessages);

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const siteData = useSelector(getSiteData);

  const getDeviceDetailLink = (deviceCompanyId: number, deviceSiteId: number, deviceId: number) =>
    PATHS.DEVICE_DETAIL.replace(':companyId', String(deviceCompanyId))
      .replace(':siteId', String(deviceSiteId))
      .replace(':deviceId', String(deviceId));

  const loadingWrapper = (children: ReactNode) =>
    isFetching ? <Skeleton animation="wave" height={25} width="80%" /> : children;

  const columns = useMemo(
    () => [
      {
        disableColumnMenu: true,
        field: 'name',
        flex: 1,
        headerName: formatMessage(messages.companyAdminDevicesDataGridColumnsDeviceName),
        renderCell: (device: GridRenderCellParams<IDevice, IDevice>) =>
          loadingWrapper(
            device?.value && (
              <Tooltip title={<DeviceModelName deviceType={device.value?.deviceType} type={device.value?.type} />}>
                <Box
                  component="span"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    overflow: 'hidden',
                  }}
                >
                  <NameColumn
                    iconComponent={<DeviceTypeIcon deviceType={device.value?.deviceType} type={device.value?.type} />}
                    name={device.value?.name}
                  />
                </Box>
              </Tooltip>
            )
          ),
        sortable: true,
      },
      {
        disableColumnMenu: true,
        field: 'apartment',
        flex: 1,
        headerName: formatMessage(messages.companyAdminDevicesDataGridColumnsApartment),
        renderCell: (device: GridRenderCellParams<IDevice, IDevice>) =>
          loadingWrapper(
            device?.value?.apartment && (
              <ApartmentNameChip
                id={undefined}
                link={undefined}
                name={device.value.apartment.name}
                number={device.value.apartment.number}
              />
            )
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'services',
        flex: 1,
        headerName: formatMessage(messages.companyAdminDevicesDataGridColumnsActiveServices),
        renderCell: (device: GridRenderCellParams<IDevice, IDevice>) =>
          loadingWrapper(
            device?.value && (
              <IconServices
                hasActivableGSMService={hasActivableGSMService(device.value as any, siteData)}
                device={device.value as any}
              />
            )
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'status',
        headerName: formatMessage(messages.companyAdminDevicesDataGridColumnsDeviceStatus),
        renderCell: (device: GridRenderCellParams<IDevice, IDevice>) =>
          loadingWrapper(device?.value && <DeviceStatus device={device.value as any} />),
        sortable: false,
        width: 170,
      },
      {
        disableColumnMenu: true,
        field: 'rowMenu',
        headerName: '',
        renderCell: (device: GridRenderCellParams<IDevice, IDevice>) =>
          loadingWrapper(
            device?.value && (
              <DataGridRowMenu
                deviceDetailLink={getDeviceDetailLink(
                  device.value?.site?.company?.id ?? 0,
                  device.value?.site?.id ?? 0,
                  device?.row?.id ?? 0
                )}
              />
            )
          ),
        sortable: false,
        width: 65,
      },
    ],
    [
      upSm,
      upMd,
      isFetching,
      isLocalizationLoaded,
      siteData,
      formatMessage,
      getDeviceDetailLink,
      hasActivableGSMService,
      loadingWrapper,
    ]
  );

  const rows = useMemo(
    () =>
      data.map((device) => ({
        apartment: device,
        id: device.id,
        isClickable: true,
        name: device,
        rowMenu: device,
        services: device,
        status: device,
      })),
    [data]
  );

  return (
    <DataGrid
      columns={columns}
      columnVisibilityModel={{
        apartment: upMd,
        services: upSm,
      }}
      dataTestId="company-admin-devices-data-grid"
      disableRowSelectionOnClick
      hideFooterPagination={isFetching}
      loading={isFetching}
      noRowsOverlay={(<NoRowsOverlay />) as any}
      onCellClick={(params: GridRenderCellParams<IDevice, IDevice>) => {
        if (params.field !== 'rowMenu') {
          if (params?.value?.id && params.value?.site?.id && params.value?.site?.company?.id) {
            navigate(getDeviceDetailLink(params.value.site.company.id, params.value.site.id, params?.row?.id ?? 0));
          }
        }
      }}
      onOrderBy={onOrderBy}
      onRowsPerPageChange={onRowsPerPageChange}
      onSelectPage={onSelectPage}
      order={order}
      page={page}
      resultsFiltered={resultsFiltered}
      resultsTotal={resultsTotal}
      rows={rows}
      rowsPerPage={rowsPerPage}
    />
  );
};

export default CompanyAdminDevicesDataGrid;
